"use client";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    EVENT_POLL_INTERVAL_MS,
    errorStatus,
    eventErrorMessage,
    isLicenseRequired,
} from "@/lib/events";
import { ClockState, EventMeRead } from "@openapi";
import { App } from "antd";
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";

// The selected event id is a per-browser convenience: it has to survive the
// full page reload that "save to cloud" triggers, and it is re-validated
// against the server on every poll. It is keyed by user so the next student
// on a shared computer does not inherit it.
const STORAGE_PREFIX = "activeCompetitionEventId:";
const storageListeners = new Set<() => void>();

// Focus and visibilitychange fire together when returning to the tab.
const MIN_POLL_GAP_MS = 2000;

const readStoredEventId = (userId: string | undefined): string | null => {
    if (!userId) return null;
    try {
        return localStorage.getItem(STORAGE_PREFIX + userId);
    } catch {
        return null;
    }
};

const writeStoredEventId = (
    userId: string | undefined,
    eventId: string | null,
) => {
    if (!userId) return;
    try {
        if (eventId) {
            localStorage.setItem(STORAGE_PREFIX + userId, eventId);
        } else {
            localStorage.removeItem(STORAGE_PREFIX + userId);
        }
    } catch {
        // Storage unavailable (private mode): event mode lasts until reload.
    }
    storageListeners.forEach((listener) => listener());
};

const subscribeStoredEventId = (listener: () => void) => {
    storageListeners.add(listener);
    window.addEventListener("storage", listener);
    return () => {
        storageListeners.delete(listener);
        window.removeEventListener("storage", listener);
    };
};

interface Snapshot {
    eventId: string;
    data: EventMeRead;
    receivedAt: number;
}

interface EventModeContextValue {
    /** Id of the event the student is in, or null in practice mode. */
    eventId: string | null;
    /** Latest server view of the event; null until the first poll returns. */
    event: EventMeRead | null;
    loading: boolean;
    error: string | null;
    /** Event last left because its organization has no valid license. */
    licenseRequiredEventId: string | null;
    /** Server clock state, switched to finished locally at the deadline. */
    clockState: ClockState | null;
    /** Local time (ms) of the deadline while running, from the last poll. */
    deadlineAt: number | null;
    /** Local time (ms) a user event opens while waiting for it, if known. */
    startsAt: number | null;
    canSubmit: boolean;
    problemIds: ReadonlySet<string>;
    enterEvent: (eventId: string) => void;
    leaveEvent: () => void;
    refresh: () => Promise<void>;
}

const EventModeContext = createContext<EventModeContextValue | undefined>(
    undefined,
);

export function EventModeProvider({ children }: { children: ReactNode }) {
    const { isLoggedIn, user } = useAuth();
    const { message } = App.useApp();
    const { t } = useTranslation();
    const userId = isLoggedIn ? user?.id : undefined;

    const getStoredEventId = useCallback(
        () => readStoredEventId(userId),
        [userId],
    );
    const eventId = useSyncExternalStore(
        subscribeStoredEventId,
        getStoredEventId,
        () => null,
    );

    const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
    const [failure, setFailure] = useState<{
        eventId: string;
        message: string;
    } | null>(null);
    const [licenseRequiredEventId, setLicenseRequiredEventId] = useState<
        string | null
    >(null);
    // The snapshot whose local countdown reached zero before the next poll.
    const [expired, setExpired] = useState<Snapshot | null>(null);

    // Only the newest request for the current event may update state.
    const activeIdRef = useRef<string | null>(eventId);
    const requestSeqRef = useRef(0);
    const lastPollRef = useRef(0);
    // Finished is final: stop the timed polls and only re-read on focus.
    const finishedRef = useRef(false);
    useEffect(() => {
        activeIdRef.current = eventId;
    }, [eventId]);

    const fetchMe = useCallback(
        async (id: string) => {
            const seq = ++requestSeqRef.current;
            lastPollRef.current = Date.now();
            const isCurrent = () =>
                seq === requestSeqRef.current && activeIdRef.current === id;
            try {
                const data = await api.events.getMyEvent({ eventId: id });
                if (!isCurrent()) return;
                setSnapshot({ eventId: id, data, receivedAt: Date.now() });
                setFailure(null);
                setLicenseRequiredEventId(null);
            } catch (error) {
                const text = await eventErrorMessage(error, t);
                const licenseRequired = await isLicenseRequired(error);
                if (!isCurrent()) return;
                const status = errorStatus(error);
                if (status === 403 || status === 404) {
                    // No longer a competitor (or the event is gone): leave.
                    // A locked organization is remembered so the event page
                    // can say why instead of "not competing".
                    setLicenseRequiredEventId(licenseRequired ? id : null);
                    writeStoredEventId(userId, null);
                    message.warning(
                        t("events.mode.leftAutomatically", { reason: text }),
                    );
                    return;
                }
                setFailure({ eventId: id, message: text });
            }
        },
        [message, t, userId],
    );

    // Poll the server view, and re-read it whenever the tab regains focus.
    useEffect(() => {
        if (!eventId) return;
        const poll = () => void fetchMe(eventId);
        const onReturn = () => {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - lastPollRef.current < MIN_POLL_GAP_MS) return;
            poll();
        };
        const timeout = setTimeout(poll, 0);
        const interval = setInterval(() => {
            // Hidden tabs catch up on focus instead.
            if (document.visibilityState !== "visible") return;
            if (finishedRef.current) return;
            poll();
        }, EVENT_POLL_INTERVAL_MS);
        window.addEventListener("focus", onReturn);
        document.addEventListener("visibilitychange", onReturn);
        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            window.removeEventListener("focus", onReturn);
            document.removeEventListener("visibilitychange", onReturn);
        };
    }, [eventId, fetchMe]);

    const current = snapshot && snapshot.eventId === eventId ? snapshot : null;
    const event = current?.data ?? null;
    useEffect(() => {
        finishedRef.current = event?.clock_state === ClockState.Finished;
    }, [event]);

    const deadlineAt =
        current &&
        current.data.clock_state === ClockState.Running &&
        current.data.seconds_remaining != null
            ? current.receivedAt + current.data.seconds_remaining * 1000
            : null;

    // At the local deadline: close submitting and confirm with the server.
    useEffect(() => {
        if (!current || deadlineAt === null) return;
        const timeout = setTimeout(
            () => {
                setExpired(current);
                void fetchMe(current.eventId);
            },
            Math.max(0, deadlineAt - Date.now()),
        );
        return () => clearTimeout(timeout);
    }, [current, deadlineAt, fetchMe]);

    // A user event starts at a known time: re-read it then, not on the next poll.
    const startsAt =
        current?.data.clock_state === ClockState.Waiting
            ? (current.data.started_at?.getTime() ?? null)
            : null;
    useEffect(() => {
        if (!eventId || startsAt === null) return;
        const delay = startsAt - Date.now() + 500;
        if (delay > EVENT_POLL_INTERVAL_MS) return;
        const timeout = setTimeout(
            () => void fetchMe(eventId),
            Math.max(0, delay),
        );
        return () => clearTimeout(timeout);
    }, [eventId, startsAt, fetchMe]);

    let clockState: ClockState | null = event?.clock_state ?? null;
    if (clockState === ClockState.Running && expired === current) {
        clockState = ClockState.Finished;
    }

    const problemIds = useMemo(
        () => new Set(event?.problems.map((p) => p.problem_id) ?? []),
        [event],
    );

    const enterEvent = useCallback(
        (id: string) => writeStoredEventId(userId, id),
        [userId],
    );

    const leaveEvent = useCallback(
        () => writeStoredEventId(userId, null),
        [userId],
    );

    const refresh = useCallback(
        () => (eventId ? fetchMe(eventId) : Promise.resolve()),
        [eventId, fetchMe],
    );

    const error =
        failure && failure.eventId === eventId ? failure.message : null;

    const value = useMemo<EventModeContextValue>(
        () => ({
            eventId,
            event,
            loading: !!eventId && !event && !error,
            error,
            licenseRequiredEventId,
            clockState,
            deadlineAt: clockState === ClockState.Running ? deadlineAt : null,
            startsAt,
            canSubmit: clockState === ClockState.Running,
            problemIds,
            enterEvent,
            leaveEvent,
            refresh,
        }),
        [
            eventId,
            event,
            error,
            licenseRequiredEventId,
            clockState,
            deadlineAt,
            startsAt,
            problemIds,
            enterEvent,
            leaveEvent,
            refresh,
        ],
    );

    return (
        <EventModeContext.Provider value={value}>
            {children}
        </EventModeContext.Provider>
    );
}

export const useEventMode = (): EventModeContextValue => {
    const context = useContext(EventModeContext);
    if (!context) {
        throw new Error(
            "useEventMode must be used within an EventModeProvider",
        );
    }
    return context;
};

// Seconds left until `target` (local ms), ticking once a second.
const useCountdownTo = (target: number | null): number | null => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (target === null) return;
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [target]);
    if (target === null) return null;
    return Math.max(0, Math.ceil((target - now) / 1000));
};

/**
 * Seconds left until the event deadline, ticking once a second. Kept out of
 * the context value so only the components showing the clock re-render.
 */
export const useEventCountdown = (): number | null =>
    useCountdownTo(useEventMode().deadlineAt);

/** Seconds until a waiting user event opens, or null when not known. */
export const useEventStartCountdown = (): number | null =>
    useCountdownTo(useEventMode().startsAt);
